import React, { useState } from "react";
import styled from "styled-components";
import { AdminButton } from "../../pages/admin/action_engine";

export default function PdfEmailModal({
  title = "Envoyer le PDF",
  description = "Le PDF sera généré et envoyé en pièce jointe.",
  defaultTo = "",
  defaultCc = "",
  defaultSubject = "",
  defaultBody = "",
  onClose,
  onSend,
}) {
  const [to, setTo] = useState(defaultTo);
  const [cc, setCc] = useState(defaultCc);
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [sending, setSending] = useState(false);
  const [localMessage, setLocalMessage] = useState("");
  const [isError, setIsError] = useState(false);

  const handleSend = async () => {
    if (!to.trim()) {
      setLocalMessage("Merci de renseigner un destinataire.");
      setIsError(true);
      return;
    }

    try {
      setSending(true);
      setLocalMessage("");
      setIsError(false);

      await onSend({
        recipient: to.trim(),
        cc: cc.trim() || null,
        subject: subject.trim(),
        body,
      });

      onClose?.();
    } catch (error) {
      console.error(error);
      setLocalMessage(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.response?.data?.msg ||
          "Impossible d’envoyer l’email."
      );
      setIsError(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <ModalOverlay onClick={() => !sending && onClose?.()}>
      <ZModal onClick={(event) => event.stopPropagation()}>
        <ModalHeader>
          <div>
            <SectionBadge>Email</SectionBadge>
            <h4>{title}</h4>
            <p>{description}</p>
          </div>

          <CloseButton
            type="button"
            onClick={onClose}
            disabled={sending}
          >
            ×
          </CloseButton>
        </ModalHeader>

        {localMessage && (
          <PanelMessage $error={isError}>
            {localMessage}
          </PanelMessage>
        )}

        <FieldsGrid>
          <Field $full>
            <label>Destinataire *</label>
            <input
              type="email"
              value={to}
              onChange={(event) => setTo(event.target.value)}
              placeholder="contact@queens-games.com"
            />
          </Field>

          <Field $full>
            <label>Copie</label>
            <input
              type="text"
              value={cc}
              onChange={(event) => setCc(event.target.value)}
              placeholder="expert-comptable@cabinet.fr"
            />
          </Field>

          <Field $full>
            <label>Objet</label>
            <input
              type="text"
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
            />
          </Field>

          <Field $full>
            <label>Message</label>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              rows={8}
            />
          </Field>
        </FieldsGrid>

        <ModalActions>
          <ActionMiniButton
            type="button"
            onClick={onClose}
            disabled={sending}
          >
            Annuler
          </ActionMiniButton>

          <ActionMiniButton
            type="button"
            onClick={handleSend}
            disabled={sending}
          >
            {sending ? "Envoi..." : "Envoyer"}
          </ActionMiniButton>
        </ModalActions>
      </ZModal>
    </ModalOverlay>
  );
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 4rem 1rem;
  background: rgba(0, 0, 0, 0.72);
  overflow-y: auto;
`;

const ZModal = styled.div`
  width: min(980px, 100%);
  max-height: calc(100vh - 6rem);
  overflow-y: auto;
  padding: 1.25rem;
  border-radius: 22px;
  background: #1d263d;
  color: #f7f7f7;
  border: 1px solid rgba(255, 255, 255, 0.12);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.2rem;

  h4 {
    margin: 0.35rem 0 0;
    font-size: 1.35rem;
  }

  p {
    margin: 0.35rem 0 0;
    opacity: 0.75;
  }
`;

const SectionBadge = styled.span`
  display: inline-block;
  width: fit-content;
  padding: 0.3rem 0.65rem;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 950;
`;

const CloseButton = styled(AdminButton)`
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 999px;
  background: #fff9ea;
  color: #0b1328;
  font-size: 1.6rem;
  font-weight: 900;
  cursor: pointer;
  line-height: 1;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const FieldsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.35rem;
  grid-column: ${({ $full }) => ($full ? "1 / -1" : "auto")};

  label {
    font-size: 0.78rem;
    font-weight: 850;
    opacity: 0.82;
  }

  input,
  textarea {
    width: 100%;
    min-height: 42px;
    border: none;
    border-radius: 10px;
    padding: 0.75rem;
    background: #fff9ea;
    color: #0b1328;
    font-weight: 750;
    box-sizing: border-box;
    outline: none;
  }

  input:focus,
  textarea:focus {
    box-shadow: 0 0 0 3px rgba(255, 249, 234, 0.22);
  }

  textarea {
    resize: vertical;
    min-height: 150px;
  }
`;

const ModalActions = styled.div`
  position: sticky;
  bottom: -1.25rem;
  display: flex;
  justify-content: flex-end;
  gap: 0.6rem;
  margin-top: 1rem;
  padding: 1rem 0 0.2rem;
  background: #1d263d;
`;

const ActionMiniButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 0.35rem 0.55rem;
  background: #fff9ea;
  color: #0b1328;
  font-size: 0.72rem;
  font-weight: 950;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PanelMessage = styled.div`
  margin-bottom: 1rem;
  padding: 0.85rem 1rem;
  border-radius: 12px;
  font-weight: 850;
  background: ${({ $error }) =>
    $error ? "rgba(180, 40, 40, 0.9)" : "rgba(40, 140, 80, 0.9)"};
`;